import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { BsArrowLeft } from "react-icons/bs";
import {
  ButtonComp,
  CardComp,
  FormikDatePicker,
  MoneyInputComp,
  SelectComp2,
  TableCompData,
} from "../../../components";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import ConfirmationModal from "../../../components/Common/ConfirmationModal";
import {
  useConfirmLockMigratedLoanMutation,
  useDeleteMigratedLoanDraftMutation,
  useGetMigratedLoanDetailQuery,
  useRecordMigratedLoanPaymentMutation,
} from "../../../store/admin/loan";
import { usePaymentMethodsQuery } from "../../../store/generalSelections/generalSelection";
import { useGetAllBankInfoQuery } from "../../../store/admin/settings/societyBankSlice";
import { convertToTitleCase } from "../../../utils/helpers";
import { formatMoney } from "../../../utils/currencyFormat";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Modal } from "react-bootstrap";

function parseMoneyAmount(value) {
  if (value == null || value === "") return NaN;
  if (typeof value === "number") return value;
  const cleaned = String(value).replace(/[₦,\s]/g, "");
  return Number(cleaned);
}

function paymentErrorMessage(err) {
  const data = err?.data;
  if (typeof data?.message === "string" && data.message) return data.message;
  const errors = data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return err?.message || "Payment failed";
}

function memberName(member) {
  if (!member) return "—";
  if (typeof member === "string") return member;
  return (
    `${member.first_name || ""} ${member.last_name || ""}`.trim() ||
    member.name ||
    "—"
  );
}

export default function RecordExistingLoanDetail() {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const [showLockModal, setShowLockModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [payingRow, setPayingRow] = useState(null);
  const [showInformalPayment, setShowInformalPayment] = useState(false);

  const { data: loan, isLoading, isFetching, refetch } =
    useGetMigratedLoanDetailQuery(loanId, { skip: !loanId });
  const [confirmLock, { isLoading: isLocking }] =
    useConfirmLockMigratedLoanMutation();
  const [recordPayment, { isLoading: isPaying }] =
    useRecordMigratedLoanPaymentMutation();
  const [deleteDraft, { isLoading: isDeleting }] =
    useDeleteMigratedLoanDraftMutation();

  const { data: paymentMethodsData } = usePaymentMethodsQuery();
  const { data: bankData, isLoading: isLoadingBank } = useGetAllBankInfoQuery({
    pageNumber: 1,
  });
  const bankOptions = useMemo(
    () =>
      (bankData?.company_banks || []).map((item) => ({
        label: item.bank_name,
        value: item.bank_name,
      })),
    [bankData]
  );

  const isInformal =
    String(loan?.loan_model || loan?.loan_type?.loan_model || "")
      .toLowerCase() === "informal";

  const breakdowns = useMemo(() => {
    const list = loan?.breakdowns || [];
    return [...list].sort((a, b) => {
      if (isInformal) {
        const da = a?.paid_at ? new Date(a.paid_at).getTime() : 0;
        const db = b?.paid_at ? new Date(b.paid_at).getTime() : 0;
        return da - db;
      }
      const da = a?.due_at ? new Date(a.due_at).getTime() : 0;
      const db = b?.due_at ? new Date(b.due_at).getTime() : 0;
      return da - db;
    });
  }, [loan, isInformal]);

  const isDraft = Boolean(loan?.is_migrated) && !loan?.migration_locked_at;

  const principal = Number(loan?.principal ?? loan?.amount ?? 0);
  const totalPrincipalPaid = breakdowns.reduce(
    (sum, row) => sum + Number(row?.principal_portion || 0),
    0
  );
  const accruedInterest = Number(loan?.accrued_interest || 0);
  const currentPrincipal = Math.max(0, principal - totalPrincipalPaid);
  const outstanding = isInformal
    ? Math.max(0, currentPrincipal + accruedInterest)
    : breakdowns
        .filter((row) => String(row?.status || "").toUpperCase() !== "PAID")
        .reduce((sum, row) => sum + Number(row?.amount || 0), 0);
  const totalPaidInformal = breakdowns.reduce(
    (sum, row) => sum + Number(row?.amount || 0),
    0
  );

  const requiresBank = (paymentMethod) => {
    if (!paymentMethod) return false;
    const methodUpper = String(paymentMethod).toUpperCase();
    return (
      methodUpper.includes("BANK") ||
      methodUpper.includes("TRANSFER") ||
      methodUpper === "CHEQUE"
    );
  };

  const staffPaymentMethods = useMemo(() => {
    const list = paymentMethodsData?.data ?? paymentMethodsData ?? [];
    const arr = Array.isArray(list) ? list : [];
    return arr
      .filter((item) => {
        const name = String(item?.name ?? item?.label ?? item?.value ?? "").toUpperCase();
        return (
          name &&
          !name.includes("PAYSTACK") &&
          !name.includes("CARD") &&
          !name.includes("ALATPAY") &&
          !name.includes("OPENING")
        );
      })
      .map((item) => ({
        label: convertToTitleCase(
          String(item.name ?? item.label ?? item.value ?? "").replace(/_/g, " ")
        ),
        value: item.name ?? item.value ?? item.id,
      }));
  }, [paymentMethodsData]);

  const handleLock = async () => {
    try {
      const result = await confirmLock(loanId).unwrap();
      toast.success(
        result?.message ||
          "Loan locked. The member can now see it on their dashboard."
      );
      setShowLockModal(false);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Lock failed");
    }
  };

  const handleDeleteDraft = async () => {
    try {
      const result = await deleteDraft(loanId).unwrap();
      toast.success(result?.message || "Migration draft deleted.");
      setShowDeleteModal(false);
      navigate("/super_admin/loan_management/record_existing_loan");
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Could not delete draft");
    }
  };

  const handleMarkPaid = async (values, { resetForm }) => {
    if (!payingRow?.id) return;
    const amount = parseMoneyAmount(values.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    const payload = {
      breakdown_id: payingRow.id,
      loan_request_id: Number(loanId),
      amount,
      payment_method: values.payment_method,
      payment_date: moment(values.payment_date).format("YYYY-MM-DD"),
    };
    if (requiresBank(values.payment_method) && values.bank) {
      payload.bank = values.bank;
    }
    try {
      await recordPayment({ loanId, payload }).unwrap();
      toast.success("Installment marked as paid");
      resetForm();
      setPayingRow(null);
      refetch();
    } catch (err) {
      toast.error(paymentErrorMessage(err));
    }
  };

  const handleInformalPayment = async (values, { resetForm }) => {
    const amount = parseMoneyAmount(values.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    const idempotencyKey =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `mig-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const payload = {
      loan_request_id: Number(loanId),
      amount,
      payment_method: values.payment_method,
      payment_date: moment(values.payment_date).format("YYYY-MM-DD"),
      idempotency_key: idempotencyKey,
    };
    if (requiresBank(values.payment_method) && values.bank) {
      payload.bank = values.bank;
    }
    try {
      await recordPayment({ loanId, payload }).unwrap();
      toast.success("Historical payment recorded");
      resetForm();
      setShowInformalPayment(false);
      refetch();
    } catch (err) {
      toast.error(paymentErrorMessage(err));
    }
  };

  const formalColumns = [
    {
      name: "INSTALLMENT",
      selector: (row) => row?.repayment_type || "—",
      sortable: false,
    },
    {
      name: "DUE DATE",
      selector: (row) =>
        row?.due_at ? moment(row.due_at).format("DD-MMM-YYYY") : "—",
      sortable: false,
    },
    {
      name: "AMOUNT",
      selector: (row) => formatMoney(Number(row?.amount || 0).toFixed(2)),
    },
    {
      name: "STATUS",
      cell: (row) => {
        const status = String(row?.status || "").toUpperCase();
        const isPaid = status === "PAID";
        return (
          <span
            className={`badge rounded-pill px-3 py-2 ${
              isPaid ? "bg-success" : "bg-warning text-dark"
            }`}
          >
            {convertToTitleCase(row?.status || "PENDING")}
          </span>
        );
      },
    },
    {
      name: "PAID AT",
      selector: (row) =>
        row?.paid_at ? moment(row.paid_at).format("DD-MMM-YYYY") : "—",
    },
    {
      name: "ACTION",
      cell: (row) => {
        if (!isDraft) return "—";
        if (String(row?.status || "").toUpperCase() === "PAID") return "—";
        return (
          <ButtonComp
            btnText="Mark paid"
            btnClassName="bg-secondary2 text-white rounded-pill px-3 py-1"
            style={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
            onClick={() => setPayingRow(row)}
          />
        );
      },
    },
  ];

  const informalColumns = [
    {
      name: "S/N",
      selector: (_, index) => index + 1,
      width: "70px",
    },
    {
      name: "DATE",
      selector: (row) =>
        row?.paid_at ? moment(row.paid_at).format("DD-MMM-YYYY") : "—",
    },
    {
      name: "AMOUNT",
      selector: (row) => formatMoney(Number(row?.amount || 0).toFixed(2)),
    },
    {
      name: "PRINCIPAL",
      selector: (row) =>
        formatMoney(Number(row?.principal_portion || 0).toFixed(2)),
    },
    {
      name: "INTEREST",
      selector: (row) =>
        formatMoney(Number(row?.interest_portion || row?.interest || 0).toFixed(2)),
    },
    {
      name: "METHOD",
      selector: (row) => convertToTitleCase(row?.payment_method || "—"),
    },
    {
      name: "BALANCE AFTER",
      selector: (row) =>
        formatMoney(Number(row?.balance_after ?? row?.ending_balance ?? 0).toFixed(2)),
    },
  ];

  if (isLoading) {
    return (
      <section className="px-lg-3 min-vh-100 pb-5 mb-3">
        <TableSkeletonLoader rows={10} columns={6} />
      </section>
    );
  }

  if (!loan) {
    return (
      <section className="px-lg-3 pb-5">
        <p>Migrated loan not found.</p>
        <Link to="/super_admin/loan_management/record_existing_loan">Back</Link>
      </section>
    );
  }

  return (
    <section className="px-lg-3 min-vh-100 pb-5 mb-3">
      <div className="d-flex align-items-center mb-4 gap-3">
        <BsArrowLeft
          className="text-xl cursor-pointer"
          role="button"
          onClick={() =>
            navigate("/super_admin/loan_management/record_existing_loan")
          }
        />
        <div>
          <h1 className="h4 fw-bold mb-1 text-black1">Existing loan detail</h1>
          <p className="text-muted mb-0" style={{ fontSize: 14 }}>
            {isDraft
              ? isInformal
                ? "Record each historical payment already made, then Confirm & lock. Until locked, the member will not see this loan."
                : "Mark installments already paid, then Confirm & lock. Until locked, the member will not see this loan."
              : "This loan is locked and treated as a normal loan going forward."}
          </p>
        </div>
      </div>

      <CardComp
        cardClassName="border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"
        cardBodyClassName="row gy-3"
      >
        <div className="col-md-4">
          <div className="text-muted small">Member</div>
          <div className="fw-semibold">{memberName(loan.member)}</div>
        </div>
        <div className="col-md-4">
          <div className="text-muted small">Membership number</div>
          <div className="fw-semibold">{loan?.member?.member_no || "—"}</div>
        </div>
        <div className="col-md-4">
          <div className="text-muted small">Loan type</div>
          <div className="fw-semibold">
            {loan?.loan_type?.name || "—"}
            {isInformal ? " (Flexible)" : ""}
          </div>
        </div>
        <div className="col-md-4">
          <div className="text-muted small">Principal</div>
          <div className="fw-semibold">
            {formatMoney(Number(loan?.amount || 0).toFixed(2))}
          </div>
        </div>
        {isInformal && (
          <>
            <div className="col-md-4">
              <div className="text-muted small">Total paid so far</div>
              <div className="fw-semibold">
                {formatMoney(Number(totalPaidInformal || 0).toFixed(2))}
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-muted small">Outstanding</div>
              <div className="fw-semibold">
                {formatMoney(Number(outstanding || 0).toFixed(2))}
              </div>
            </div>
          </>
        )}
        <div className="col-md-4">
          <div className="text-muted small">Approved</div>
          <div className="fw-semibold">
            {loan?.approved_at
              ? moment(loan.approved_at).format("DD-MMM-YYYY")
              : "—"}
          </div>
        </div>
        <div className="col-md-4">
          <div className="text-muted small">Disbursed</div>
          <div className="fw-semibold">
            {loan?.disbursed_at
              ? moment(loan.disbursed_at).format("DD-MMM-YYYY")
              : "—"}
          </div>
        </div>
        <div className="col-md-4">
          <div className="text-muted small">Migration</div>
          <div>
            <span
              className={`badge rounded-pill px-3 py-2 ${
                isDraft ? "bg-warning text-dark" : "bg-success"
              }`}
            >
              {isDraft ? "Draft (member cannot see)" : "Locked (member can see)"}
            </span>
          </div>
        </div>
      </CardComp>

      <CardComp
        cardClassName="border-0 rounded-10 px-0 mb-4 shadow3"
        cardBodyClassName="py-0 px-0"
      >
        <div className="px-4 pt-4 pb-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h5 className="fw-bold mb-0 text-black1">
            {isInformal ? "Historical payments" : "Repayment schedule"}
          </h5>
          <div className="d-flex flex-wrap gap-2">
            {isDraft && isInformal && outstanding > 0 && (
              <ButtonComp
                btnText="Add historical payment"
                btnClassName="bg-secondary2 text-white rounded px-4 py-2"
                onClick={() => setShowInformalPayment(true)}
              />
            )}
            {isDraft && (
              <ButtonComp
                btnText="Confirm & lock"
                btnClassName="bg-primary1 text-white rounded px-4 py-2"
                onClick={() => setShowLockModal(true)}
              />
            )}
            {isDraft && (
              <ButtonComp
                btnText="Delete draft"
                btnClassName="bg-danger text-white rounded px-4 py-2"
                onClick={() => setShowDeleteModal(true)}
              />
            )}
          </div>
        </div>
        <TableCompData
          columns={isInformal ? informalColumns : formalColumns}
          data={breakdowns}
          // Preserve due-date / paid-at order from `breakdowns` (string sort puts "month 10" before "month 2").
          defaultSortFieldId={false}
          progressPending={isFetching}
          progressComponent={<TableSkeletonLoader rows={8} columns={6} />}
          noDataComponent={
            <div className="p-4 text-muted">
              {isInformal
                ? "No historical payments recorded yet. Add each payment already made before locking."
                : "No repayment schedule found."}
            </div>
          }
        />
      </CardComp>

      <ConfirmationModal
        isOpen={showLockModal}
        onClose={() => setShowLockModal(false)}
        title="Confirm & lock this loan?"
        message="If you do not lock this loan, the member will not see it on their dashboard. After lock, this loan is treated as a normal loan and cannot be migrated again."
        confirmText={isLocking ? "Locking…" : "Confirm & lock"}
        confirmButtonClass="bg-primary1 text-white"
        onConfirm={handleLock}
        isLoading={isLocking}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete migration draft?"
        message="This permanently removes the draft loan, its schedule, and any historical payments recorded for migration. You can record the loan again later if needed."
        confirmText={isDeleting ? "Deleting…" : "Delete draft"}
        confirmButtonClass="bg-danger text-white"
        onConfirm={handleDeleteDraft}
        isLoading={isDeleting}
      />

      {/* Formal mark-paid modal */}
      <Modal show={Boolean(payingRow)} onHide={() => setPayingRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Mark installment paid</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Record the historical payment for{" "}
            <strong>{payingRow?.repayment_type}</strong>. This creates a
            transaction history entry like a normal staff-recorded repayment.
          </p>
          <Formik
            enableReinitialize
            initialValues={{
              amount: payingRow?.amount || "",
              payment_method: "",
              payment_date: payingRow?.due_at
                ? moment(payingRow.due_at).format("YYYY-MM-DD")
                : moment().format("YYYY-MM-DD"),
              bank: "",
            }}
            validationSchema={Yup.object({
              amount: Yup.string()
                .required("Required")
                .test("valid-amount", "Enter a valid amount", (value) => {
                  const n = parseMoneyAmount(value);
                  return Number.isFinite(n) && n > 0;
                }),
              payment_method: Yup.string().required("Required"),
              payment_date: Yup.string().required("Required"),
              bank: Yup.string().when("payment_method", {
                is: (value) => requiresBank(value),
                then: (schema) => schema.required("Bank is required"),
                otherwise: (schema) => schema.notRequired(),
              }),
            })}
            onSubmit={handleMarkPaid}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form className="space-y-3">
                <MoneyInputComp
                  LabelText="Amount"
                  name="amount"
                  disabled
                  LabelClassName="fw-3"
                />
                <SelectComp2
                  labelText="Payment method"
                  LabelClassName="fw-3"
                  placeholder="Select payment method"
                  arrayComp={staffPaymentMethods}
                  selectedOption={values.payment_method}
                  setSelectedOption={(opt) => {
                    const selectedValue = opt?.value || "";
                    setFieldValue("payment_method", selectedValue);
                    if (!requiresBank(selectedValue)) {
                      setFieldValue("bank", "");
                    }
                  }}
                  errors={
                    touched.payment_method && errors.payment_method
                      ? [errors.payment_method]
                      : []
                  }
                />
                {requiresBank(values.payment_method) && (
                  <SelectComp2
                    labelText="Bank paid to"
                    LabelClassName="fw-3"
                    placeholder="Select bank"
                    arrayComp={bankOptions}
                    isLoading={isLoadingBank}
                    selectedOption={values.bank}
                    setSelectedOption={(opt) =>
                      setFieldValue("bank", opt?.value || "")
                    }
                    errors={touched.bank && errors.bank ? [errors.bank] : []}
                  />
                )}
                <FormikDatePicker
                  fieldName="payment_date"
                  labelText="Payment date"
                  labelClassName="fw-3"
                />
                <div className="d-flex justify-content-end align-items-center gap-2 pt-3">
                  <ButtonComp
                    type="button"
                    btnText="Cancel"
                    containerClassName="w-auto"
                    btnClassName="btn btn-outline-secondary px-4 py-2"
                    ButtonInlineStyle={{
                      minWidth: "fit-content",
                      borderRadius: 10,
                    }}
                    onClick={() => setPayingRow(null)}
                  />
                  <ButtonComp
                    type="submit"
                    btnText={isPaying ? "Saving…" : "Save payment"}
                    containerClassName="w-auto"
                    btnClassName="bg-primary1 text-white border-0 px-4 py-2"
                    ButtonInlineStyle={{
                      minWidth: "fit-content",
                      borderRadius: 10,
                      lineHeight: 1.4,
                    }}
                    loading={isPaying}
                    disabled={isPaying}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      {/* Informal historical payment modal */}
      <Modal
        show={showInformalPayment}
        onHide={() => setShowInformalPayment(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add historical payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Record one payment that was already made before UniqueCoop. You can
            add several payments for a clear history. Outstanding balance:{" "}
            <strong>{formatMoney(Number(outstanding || 0).toFixed(2))}</strong>
          </p>
          <Formik
            enableReinitialize
            initialValues={{
              amount: "",
              payment_method: "",
              payment_date: moment().format("YYYY-MM-DD"),
              bank: "",
            }}
            validationSchema={Yup.object({
              amount: Yup.string()
                .required("Required")
                .test("valid-amount", "Enter a valid amount", (value) => {
                  const n = parseMoneyAmount(value);
                  return Number.isFinite(n) && n > 0;
                })
                .test(
                  "max-outstanding",
                  `Amount cannot exceed outstanding balance (${formatMoney(
                    Number(outstanding || 0).toFixed(2)
                  )})`,
                  (value) => {
                    if (!(outstanding > 0)) return true;
                    const n = parseMoneyAmount(value);
                    return !Number.isFinite(n) || n <= outstanding;
                  }
                ),
              payment_method: Yup.string().required("Required"),
              payment_date: Yup.string().required("Required"),
              bank: Yup.string().when("payment_method", {
                is: (value) => requiresBank(value),
                then: (schema) => schema.required("Bank is required"),
                otherwise: (schema) => schema.notRequired(),
              }),
            })}
            onSubmit={handleInformalPayment}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form className="space-y-3">
                <MoneyInputComp
                  LabelText="Amount"
                  name="amount"
                  LabelClassName="fw-3"
                />
                <SelectComp2
                  labelText="Payment method"
                  LabelClassName="fw-3"
                  placeholder="Select payment method"
                  arrayComp={staffPaymentMethods}
                  selectedOption={values.payment_method}
                  setSelectedOption={(opt) => {
                    const selectedValue = opt?.value || "";
                    setFieldValue("payment_method", selectedValue);
                    if (!requiresBank(selectedValue)) {
                      setFieldValue("bank", "");
                    }
                  }}
                  errors={
                    touched.payment_method && errors.payment_method
                      ? [errors.payment_method]
                      : []
                  }
                />
                {requiresBank(values.payment_method) && (
                  <SelectComp2
                    labelText="Bank paid to"
                    LabelClassName="fw-3"
                    placeholder="Select bank"
                    arrayComp={bankOptions}
                    isLoading={isLoadingBank}
                    selectedOption={values.bank}
                    setSelectedOption={(opt) =>
                      setFieldValue("bank", opt?.value || "")
                    }
                    errors={touched.bank && errors.bank ? [errors.bank] : []}
                  />
                )}
                <FormikDatePicker
                  fieldName="payment_date"
                  labelText="Payment date"
                  labelClassName="fw-3"
                />
                <div className="d-flex justify-content-end align-items-center gap-2 pt-3">
                  <ButtonComp
                    type="button"
                    btnText="Cancel"
                    containerClassName="w-auto"
                    btnClassName="btn btn-outline-secondary px-4 py-2"
                    ButtonInlineStyle={{
                      minWidth: "fit-content",
                      borderRadius: 10,
                    }}
                    onClick={() => setShowInformalPayment(false)}
                  />
                  <ButtonComp
                    type="submit"
                    btnText={isPaying ? "Saving…" : "Save payment"}
                    containerClassName="w-auto"
                    btnClassName="bg-primary1 text-white border-0 px-4 py-2"
                    ButtonInlineStyle={{
                      minWidth: "fit-content",
                      borderRadius: 10,
                      lineHeight: 1.4,
                    }}
                    loading={isPaying}
                    disabled={isPaying}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </section>
  );
}
